import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/client';
import { useApi, useDebounced, formatDate } from '../hooks/useApi';
import {
  Card, StatusPill, ChannelTag, Pager, EmptyState, ErrorState, TableSkeleton,
} from '../components/ui';

export default function Subscribers() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const q = useDebounced(search);

  const params = useMemo(() => ({
    page, limit: 25,
    q: q || undefined,
    status: status === 'all' ? undefined : status,
  }), [page, q, status]);

  const list = useApi('/api/admin/subscribers', params);
  const summary = list.meta?.summary;

  const setFilter = (setter) => (value) => { setter(value); setPage(1); };

  /** The export endpoint needs auth, so fetch it as a blob rather than linking. */
  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const res = await api.get('/api/admin/subscribers/export', {
        params: { status: status === 'all' ? undefined : status },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `starparth-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded.');
    } catch (err) {
      toast.error(err.message || 'The export could not be generated.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="spa-stack">
      {summary && (
        <div className="spa-metrics">
          <div className="spa-metric">
            <div className="spa-metric__label">Matching</div>
            <div className="spa-metric__value">{summary.total}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Active</div>
            <div className="spa-metric__value" style={{ color: '#067647' }}>{summary.active_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Unsubscribed</div>
            <div className="spa-metric__value" style={{ color: '#98a2b3' }}>
              {summary.unsubscribed_count}
            </div>
          </div>
        </div>
      )}

      <div className="spa-toolbar">
        <div className="spa-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            className="spa-input" type="search" placeholder="Search email"
            value={search}
            onChange={(e) => setFilter(setSearch)(e.target.value)}
            aria-label="Search subscribers"
          />
        </div>

        <select className="spa-select" value={status}
          onChange={(e) => setFilter(setStatus)(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
        </select>

        <button
          type="button"
          className="spa-btn spa-btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={exportCsv}
          disabled={isExporting}
        >
          <i className={`fa-solid ${isExporting ? 'fa-spinner fa-spin' : 'fa-file-csv'}`} aria-hidden="true" />
          {isExporting ? 'Preparing…' : 'Export CSV'}
        </button>
      </div>

      <Card padded={false}>
        {list.error ? (
          <ErrorState message={list.error} onRetry={list.reload} />
        ) : list.isLoading && !list.data ? (
          <TableSkeleton cols={5} />
        ) : !list.data?.length ? (
          <EmptyState
            icon="fa-envelope"
            title="No subscribers match these filters"
            text="Signups from the newsletter form in the site footer appear here."
          />
        ) : (
          <>
            <div className="spa-tablewrap">
              <table className="spa-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Signed up from</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th className="num">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((s) => (
                    <tr key={s.id}>
                      <td className="spa-table__primary">{s.email}</td>
                      <td><span className="spa-pill spa-pill--neutral">{s.source_page}</span></td>
                      <td><ChannelTag channel={s.channel} /></td>
                      <td>
                        <StatusPill status={s.status} />
                        {s.unsubscribed_at && (
                          <div className="spa-table__sub">
                            Left {formatDate(s.unsubscribed_at)}
                          </div>
                        )}
                      </td>
                      <td className="num">{formatDate(s.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager pagination={list.meta?.pagination} onPage={setPage} />
          </>
        )}
      </Card>

      <p className="spa-card__note">
        Every subscriber has a one-click unsubscribe link in their emails. Removals are
        recorded here rather than deleting the row, so the same address is not re-imported later.
      </p>
    </div>
  );
}
